import { Module } from "@/constants";
import { fetchSystemRoleList } from "@/services/systemRoles";
import { ComponentProps, forwardRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import AsyncSelect, { Option, type AsyncSelectBaseProps } from "./AsyncSelect";
import { SystemRoleCodesType } from "@/types/user";
import { FilterOperator, FilterType, mergeFilters } from "@/lib/filters";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { ReadSystemRole } from "@/types/systemRole";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { buildMultipleFilterValues } from "@/lib/filters/buildMultipleFilterValues";
import appConfiguration from "./cfg/AppCfg";

interface SystemRoleSelectProps extends AsyncSelectBaseProps {
  inDialog?: boolean;
  defaultItem?: Partial<Option>;
  excludeSystemRoleCodes?: SystemRoleCodesType | SystemRoleCodesType[];
}

export default forwardRef<HTMLButtonElement, SystemRoleSelectProps>(
  function SystemRoleSelect(
    { value, inDialog, defaultItem, excludeSystemRoleCodes, ...props },
    ref,
  ) {
    const { t } = useTranslation();

    const name = Module.SystemRoles;
    const options = useCreateDataTableOptions<ReadSystemRole>(name);
    const queryKey = useDynamicQueryParams<ReadSystemRole>(name, options, {
      code: excludeSystemRoleCodes,
    });

    const onFilter: ComponentProps<typeof AsyncSelect>["onChange"] =
      useCallback(
        (filterValue) => {
          options.setFilterValue("name", {
            value: filterValue,
            filter: FilterType.IncludesString,
          });
        },
        [options],
      );

    const filters = useMemo(
      () =>
        excludeSystemRoleCodes
          ? mergeFilters(options.filters, [
              {
                id: "code",
                value: {
                  value: buildMultipleFilterValues(
                    excludeSystemRoleCodes,
                    FilterOperator.OR,
                  ),
                  filter: FilterType.NotEquals,
                },
              },
            ])
          : options.filters,
      [options.filters, excludeSystemRoleCodes],
    );

    return (
      <AsyncSelect
        queryKey={queryKey}
        queryFn={() => {
          return fetchSystemRoleList({
            filters,
            limit: appConfiguration.selectComponentsPaginationLimit,
          });
        }}
        placeholder={t("systemRoleSelect.placeholder")}
        loadingLabel={t("systemRoleSelect.loading")}
        noResultsLabel={t("systemRoleSelect.noResults")}
        searchLabel={t("systemRoleSelect.search")}
        enableFilter
        onFilter={onFilter}
        {...props}
        value={value ? value.toString() : undefined}
        defaultItem={defaultItem}
        ref={ref}
        inDialog={inDialog}
      />
    );
  },
);
